import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Loader2 } from "lucide-react";
import CleaningSettingsCard from "@/components/CleaningSettings";
import { parseAiContent } from "@/lib/parseAiSettings";
import { Ion } from "@/components/Ion";
import { useTheme } from "@/lib/theme";

// Markdown styles from makeAiMdStyles in frontend/app/ai-assistant.tsx
const md = {
  p: ({ node, ...p }) => <p className="mb-2.5 mt-0 text-sm leading-[21px] last:mb-0" {...p} />,
  ul: ({ node, ...p }) => <ul className="mb-1.5 mt-0.5 list-disc pl-5 marker:text-[var(--accent)]" {...p} />,
  ol: ({ node, ...p }) => <ol className="mb-1.5 mt-0.5 list-decimal pl-5 marker:font-bold marker:text-[var(--accent)]" {...p} />,
  li: ({ node, ...p }) => <li className="my-px text-sm leading-[21px]" {...p} />,
  strong: ({ node, ...p }) => <strong className="font-bold" {...p} />,
  em: ({ node, ...p }) => <em className="italic" {...p} />,
  h1: ({ node, ...p }) => <h3 className="mb-1.5 mt-2 text-lg font-bold" {...p} />,
  h2: ({ node, ...p }) => <h3 className="mb-1 mt-2 text-base font-bold" {...p} />,
  h3: ({ node, ...p }) => <h4 className="mb-1 mt-1.5 text-sm font-bold tracking-[0.3px] text-[var(--accent)]" {...p} />,
  h4: ({ node, ...p }) => <h5 className="mb-0.5 mt-1 text-[13px] font-bold text-[var(--accent)]" {...p} />,
  hr: () => <hr className="my-2 h-px border-0 bg-[#333]" />,
  blockquote: ({ node, ...p }) => <blockquote className="my-1 border-l-[3px] border-[var(--accent)] bg-[#242424] py-1 pl-2.5" {...p} />,
  a: ({ node, ...p }) => <a className="text-[var(--accent)] underline" target="_blank" rel="noreferrer" {...p} />,
  table: ({ node, ...p }) => <div className="my-1.5 overflow-x-auto rounded-md border border-[#333]"><table className="w-full border-collapse" {...p} /></div>,
  th: ({ node, ...p }) => <th className="p-1.5 text-left font-bold text-[var(--accent)]" {...p} />,
  td: ({ node, ...p }) => <td className="p-1.5" {...p} />,
  code: ({ node, ...p }) => <code className="rounded bg-[#242424] px-1 text-[13px] text-[var(--accent)]" {...p} />,
  pre: ({ node, ...p }) => <pre className="my-1 overflow-x-auto whitespace-pre-wrap rounded-md bg-[#242424] p-2 text-xs [&>code]:bg-transparent [&>code]:p-0 [&>code]:text-white" {...p} />,
};

// Clean up LLM output: drop stray confidence envelopes, turn "•" lines into list items, keep single line breaks.
export const tidyAnswer = (t = "") =>
  t.replace(/^\s*\[\[CONFIDENCE[^\]]*\]\]\s*/i, "")
    .replace(/\s*«\{[^»]*\}»\s*$/, "")
    .replace(/^[ \t]*[•·][ \t]+/gm, "- ")
    .replace(/([^\n])\n(?!\n)/g, "$1  \n");

const RATED_KEY = "alr_rated_sessions";
export const isRated = (sid) => JSON.parse(localStorage.getItem(RATED_KEY) || "[]").includes(sid);
export const markRated = (sid) => localStorage.setItem(RATED_KEY, JSON.stringify([...new Set([...JSON.parse(localStorage.getItem(RATED_KEY) || "[]"), sid])].slice(-500)));

const dayKey = (iso) => (iso ? new Date(iso).toDateString() : new Date().toDateString());
const dayLabel = (key) => new Date(key).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });

const Thinking = ({ stats }) => {
  const theme = useTheme();
  const [ms, setMs] = useState(0);
  useEffect(() => { const t0 = Date.now(); const id = setInterval(() => setMs(Date.now() - t0), 250); return () => clearInterval(id); }, []);
  const s = ms / 1000;
  const target = Math.max(8, stats.p90_seconds);
  const label = s < 4 ? "Thinking…" : s < 15 ? "Searching the knowledge base…" : s < 30 ? "Working on a thorough answer…" : s < 60 ? "Cross-checking the safety guidance…" : "Almost there — complex query…";
  const m = Math.floor(s / 60), sec = Math.floor(s % 60);
  return (
    <div className="mb-2 max-w-[92%] self-start rounded-[14px] border p-4" style={{ background: theme.card, borderColor: theme.border }} data-testid="chat-thinking">
      <div className="mb-2 flex items-center gap-2">
        <Loader2 size={18} className="animate-spin" style={{ color: theme.accent }} />
        <span className="flex-1 truncate text-[13px] font-bold">{label}</span>
        <span className="rounded-full border px-2 py-[3px] text-[10px] font-bold tracking-[0.5px]" style={{ borderColor: theme.border, background: theme.surface, color: theme.textSecondary }}>{stats.is_estimate ? `~${stats.avg_seconds}s typical` : `~${stats.avg_seconds}s avg`}</span>
      </div>
      <div className="my-1.5 h-1 overflow-hidden rounded-sm" style={{ background: theme.surface }}><div className="h-full rounded-sm" style={{ width: `${Math.round(Math.min(0.95, s / target) * 100)}%`, background: theme.accent }} /></div>
      <p className="mt-1 text-[11px] tabular-nums" style={{ color: theme.textSecondary }}>{m > 0 ? `${m}:${String(sec).padStart(2, "0")}` : `${sec}s`}</p>
    </div>
  );
};

const Bubble = ({ m, i, onRate, theme }) => {
  const mine = m.role === "user";
  return (
    <div data-testid={`chat-msg-${m.role}-${i}`} className={`mb-2 max-w-[92%] rounded-[14px] p-4 ${mine ? "self-end" : "self-start border"}`} style={mine ? { background: theme.accent } : { background: theme.card, borderColor: theme.border }}>
      {m.image && <img src={m.image} alt="Attached" className="mb-2 h-[180px] w-[240px] max-w-full rounded-[10px] object-cover" />}
      {mine ? <p className="whitespace-pre-wrap text-sm leading-5 text-black">{m.text}</p>
        : parseAiContent(m.text).map((seg, si) => seg.kind === "settings" ? <CleaningSettingsCard key={si} data={seg.data} /> : <div key={si} className="text-sm leading-[21px] text-white"><ReactMarkdown remarkPlugins={[remarkGfm]} components={md}>{tidyAnswer(seg.text)}</ReactMarkdown></div>)}
      {!mine && onRate && m.sessionId && !(m.feedbackSent || isRated(m.sessionId)) && (
        <div className="mt-2 flex items-center gap-2 border-t pt-2" style={{ borderColor: theme.border }}>
          <span className="flex-1 text-[11px]" style={{ color: theme.textSecondary }}>Was this helpful?</span>
          <button onClick={() => onRate(i, "up")} aria-label="Helpful" data-testid={`chat-rate-up-${i}`} className="flex h-[30px] w-[30px] items-center justify-center rounded-lg border" style={{ background: theme.surface, borderColor: theme.border }}><Ion name="thumbs-up-outline" size={16} color={theme.accent} /></button>
          <button onClick={() => onRate(i, "down")} aria-label="Not helpful" data-testid={`chat-rate-down-${i}`} className="flex h-[30px] w-[30px] items-center justify-center rounded-lg border" style={{ background: theme.surface, borderColor: theme.border }}><Ion name="thumbs-down-outline" size={16} color={theme.textSecondary} /></button>
        </div>
      )}
      {!mine && (m.feedbackSent || (m.sessionId && isRated(m.sessionId))) && <p className="mt-2 text-[11px] font-bold" style={{ color: theme.accent }} data-testid={`chat-feedback-thanks-${i}`}>✓ Thanks — feedback saved</p>}
    </div>
  );
};

export const ChatThread = ({ messages, busy, onRate, empty, stats = { avg_seconds: 12, p90_seconds: 22, is_estimate: true } }) => {
  const theme = useTheme();
  const endRef = useRef(null);
  const [open, setOpen] = useState({});
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [messages.length, busy]);
  if (!messages.length && !busy) return empty;
  const today = new Date().toDateString();
  const groups = [];
  messages.forEach((m, i) => { const k = dayKey(m.createdAt); const g = groups[groups.length - 1]; if (g && g.key === k) g.entries.push([m, i]); else groups.push({ key: k, entries: [[m, i]] }); });
  return (
    <div className="flex flex-col" data-testid="chat-thread">
      {groups.map((g) => {
        const isToday = g.key === today;
        const expanded = isToday || open[g.key];
        return (
          <div key={g.key} className="mb-1 flex flex-col">
            {!isToday && (
              <button onClick={() => setOpen((o) => ({ ...o, [g.key]: !o[g.key] }))} data-testid={`day-toggle-${g.key.replace(/\s/g, "-")}`} className="my-2 flex max-w-[85%] items-center gap-2 self-center rounded-full border px-4 py-2.5" style={{ background: theme.card, borderColor: theme.border }}>
                <Ion name="calendar-outline" size={14} color={theme.accent} />
                <span className="text-[13px] font-bold tracking-[0.3px]">{dayLabel(g.key)}</span>
                <span className="ml-1 rounded-full border px-2 py-0.5 text-[10px] font-extrabold tracking-[0.5px]" style={{ background: `${theme.accent}1F`, borderColor: theme.accent, color: theme.accent }}>{g.entries.length} {g.entries.length === 1 ? "msg" : "msgs"}</span>
                <span className="w-4" />
                <Ion name={expanded ? "chevron-up" : "chevron-down"} size={16} color={theme.textSecondary} />
              </button>
            )}
            {expanded && g.entries.map(([m, i]) => <Bubble key={i} m={m} i={i} onRate={onRate} theme={theme} />)}
          </div>
        );
      })}
      {busy && <Thinking stats={stats} />}
      <div ref={endRef} />
    </div>
  );
};

export const ChatComposer = ({ value, onChange, onSend, busy, placeholder, image, onImage, onClearImage }) => {
  const theme = useTheme();
  const fileRef = useRef(null);
  const pick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const fr = new FileReader();
    fr.onload = () => onImage(String(fr.result));
    fr.readAsDataURL(f);
  };
  return (
    <div className="sticky bottom-0 border-t p-4" style={{ borderColor: theme.border, background: theme.bg, bottom: "var(--lso-banner-h, 0px)" }}>
      {image && (
        <div className="absolute bottom-[76px] left-4 z-[5]" data-testid="chat-attached-image">
          <img src={image} alt="To send" className="h-20 w-20 rounded-[10px] border object-cover" style={{ borderColor: theme.accent, background: theme.card }} />
          <button onClick={onClearImage} aria-label="Remove photo" data-testid="chat-remove-image" className="absolute -right-2 -top-2 rounded-full bg-black/80"><Ion name="close-circle" size={22} color="#fff" /></button>
        </div>
      )}
      <form onSubmit={(e) => { e.preventDefault(); onSend(); }} className="flex items-end gap-2">
        {onImage && (
          <>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={pick} data-testid="chat-image-input" />
            <button type="button" onClick={() => fileRef.current?.click()} aria-label="Attach photo" data-testid="chat-attach-btn" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] border" style={{ borderColor: theme.border, background: theme.card }}>
              <Ion name={image ? "image" : "camera-outline"} size={20} color={image ? theme.accent : theme.text} />
            </button>
          </>
        )}
        <textarea data-testid="chat-input" rows={1} value={value} onChange={(e) => onChange(e.target.value)} placeholder={image ? "Describe what you tried, or just tap Send…" : placeholder}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onSend(); } }}
          className="max-h-[100px] flex-1 resize-none rounded-lg border px-4 py-3 text-sm text-white outline-none placeholder:text-[#A0A0A0]" style={{ background: theme.card, borderColor: theme.border }} />
        <button type="submit" disabled={busy || (!value.trim() && !image)} aria-label="Send" data-testid="chat-send-btn" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full disabled:opacity-50" style={{ background: theme.accent }}>
          {busy ? <Loader2 size={18} className="animate-spin text-black" /> : <Ion name="arrow-up" size={20} color="#000" />}
        </button>
      </form>
    </div>
  );
};
